import React from 'react';
import { config } from '../../config';
import pastorImage from '../../assets/pastor.jpg';
import pastorWilliamImage from '../../assets/pastor-william.jpg';
import pastorPriscillaImage from '../../assets/pastor-priscilla.jpg';

export const About = () => {
  return (
    <section id="about" className="section-padding bg-accent overflow-hidden">
      <div className="container-custom">
        {/* Intro Section & Global Lead Pastor */}
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-32">
          <div className="relative">
            <div className="absolute -top-12 -left-12 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
            <p className="text-primary font-bold tracking-[0.3em] uppercase text-xs mb-6">Who We Are</p>
            <h2 className="text-4xl sm:text-6xl font-bold mb-8 leading-tight">
              A Legacy of <span className="text-primary italic font-light">Faith</span> & Community
            </h2>
            <p className="text-lg text-secondary/70 leading-relaxed mb-8 text-balance">
              {config.church_name} is more than just a building; it's a family. We are dedicated to teaching the Word of God and demonstrating the love of Christ through practical Christian living.
            </p>
            
            <div className="space-y-6 text-secondary/70 leading-relaxed mb-12">
              <p>Apostle Justice Kwawu is a thought-provoking teacher and preacher of the Gospel of Jesus Christ, serving as an apostolic voice to the Body of Christ in these end times. He is a sought-after motivational speaker and author of several inspiring books.</p>
              <p>His ministry is marked by the powerful outpouring of the Holy Spirit, with evidence of healing and miraculous works of God. He holds a Master of Science in Actuarial Science from Heriot-Watt University in Edinburgh, UK.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div className="p-6 bg-white rounded-2xl shadow-sm border border-secondary/5">
                <h4 className="font-bold text-primary mb-2">Our Mission</h4>
                <p className="text-sm text-secondary/60">Reaching, teaching, growing, and serving people for the Kingdom.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-sm border border-secondary/5">
                <h4 className="font-bold text-primary mb-2">Our Vision</h4>
                <p className="text-sm text-secondary/60">Empowering believers to work the works of God through faith.</p>
              </div>
            </div>
          </div>
          <div className="relative lg:sticky lg:top-32">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-700">
              <img src={pastorImage} alt="Apostle Justice Kwawu" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-8 glass-panel p-8 rounded-2xl max-w-xs border-2 border-primary/20">
              <h3 className="text-xl font-bold mb-1">Apostle Justice Kwawu</h3>
              <p className="text-amber-600 text-sm font-black tracking-widest uppercase drop-shadow-sm">Global Lead Pastor</p>
            </div>
          </div>
        </div>

        {/* Leadership Section */}
        <div className="mb-32">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-5xl font-bold mb-4">Branch Pastors</h2>
            <div className="w-12 h-1 bg-primary mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Reverend William Yeboah */}
            <div className="group">
              <div className="relative mb-8 overflow-hidden rounded-3xl aspect-square">
                <img 
                  src={pastorWilliamImage}
                  alt="Reverend William Yeboah"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-110 group-hover:scale-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <h3 className="text-2xl font-bold mb-4">Reverend William Yeboah</h3>
              <p className="text-secondary/70 leading-relaxed">
                Reverend William serves with a heart of compassion and dedication to God's Word. He is passionate about discipleship, spiritual growth, and equipping believers to live victorious Christian lives. His teaching ministry focuses on practical application of Scripture and building strong foundations of faith.
              </p>
            </div>

            {/* Reverend Priscilla Yeboah */}
            <div className="group">
              <div className="relative mb-8 overflow-hidden rounded-3xl aspect-square">
                <img 
                  src={pastorPriscillaImage}
                  alt="Reverend Priscilla Yeboah"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-110 group-hover:scale-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <h3 className="text-2xl font-bold mb-4">Reverend Priscilla Yeboah</h3>
              <p className="text-secondary/70 leading-relaxed">
                Reverend Priscilla brings a warm and nurturing spirit to ministry, with a special heart for prayer, worship, and women's empowerment. She is committed to creating a welcoming environment where families can flourish and believers can experience God's transforming love. Her ministry emphasizes the power of intercession and spiritual intimacy with God.
              </p>
            </div>
          </div>
        </div>

        {/* Statement of Faith */}
        <div className="glass-panel-dark rounded-[3rem] p-12 sm:p-20 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[120px] rounded-full"></div>
          <div className="relative z-10 grid lg:grid-cols-3 gap-12 items-center">
            <div className="lg:col-span-1">
              <h3 className="text-3xl sm:text-5xl font-bold mb-6">What We <br /><span className="text-primary italic font-light">Believe</span></h3>
              <p className="text-white/60">Our core values and statement of faith that guide everything we do.</p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
              {[
                "In one God, eternally existing as Father, Son, and Holy Spirit",
                "In the Bible as the inspired Word of God",
                "In salvation through faith in Jesus Christ",
                "In the power of the Holy Spirit for holy living"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm text-white/80">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
